import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import "./um.css";

const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// Read-only field
const DataField = ({ label, value, mono }) => (
  <div>
    <div className="um-fd__label">{label}</div>
    <div className={`um-fd__value ${mono ? "um-mono" : ""}`}>{value || "—"}</div>
  </div>
);

const UserManagementShow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    if (!location?.state) {
      navigate("/admin/dashboard");
    } else {
      const state = location.state;

      // Extract user info (from subAdmin inside permissions[0] if available)
      const subAdmin = state?.permissions?.[0]?.subAdmin || {};
      setUser({
        name: subAdmin?.name || state?.name,
        email: subAdmin?.email || state?.email,
        phone: state?.phone,
        designation: subAdmin?.designation || state?.designation,
        role: state?.role || "Sub Admin",
        created_at: state?.created_at || state?.permissions?.[0]?.created_at,
        status: state?.status,
      });

      // Permissions list
      setPermissions(state?.permissions || []);
    }
  }, [location, navigate]);

  const moduleNames = useMemo(
    () => [...new Set((permissions || []).map((p) => p.module_name).filter(Boolean))],
    [permissions]
  );
  const subModuleNames = useMemo(
    () => [...new Set((permissions || []).map((p) => p.sub_module_name).filter(Boolean))],
    [permissions]
  );

  const statusActive = user?.status === true || user?.status === "active";
  const statusInactive = user?.status === false || user?.status === "inactive";

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
      : "—";
  const addedDate = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <div className="um">
      <Toaster position="top-center" reverseOrder={false} />

      {/* Page header */}
      <div className="um-head">
        <div>
          <h1 className="um-title">Sub Admin Details</h1>
          <p className="um-sub">Profile, role and module access for this team member.</p>
        </div>
        <div className="um-head__actions">
          <button type="button" className="um-btn" onClick={() => navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <button
            type="button"
            className="um-btn um-btn--primary"
            onClick={() =>
              navigate("/admin/usermanagement/edit", { state: location.state })
            }
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
            </svg>
            Edit
          </button>
        </div>
      </div>

      {/* Identity banner */}
      <div className="um-banner">
        <div className="um-banner__avatar">{initialsOf(user?.name)}</div>
        <div style={{ minWidth: 0 }}>
          <div className="um-banner__row">
            <h2 className="um-banner__name">{user?.name || "—"}</h2>
            <span className="um-pill" style={{ background: "var(--color-violet-bg)", color: "var(--color-violet-text)" }}>
              {user?.role || "Sub Admin"}
            </span>
            {user?.designation && (
              <span className="um-pill" style={{ background: "var(--color-info-bg)", color: "var(--color-info-text)" }}>
                {user.designation}
              </span>
            )}
            {statusActive && (
              <span className="um-pill um-pill--ok"><span className="um-dot" />Active</span>
            )}
            {statusInactive && <span className="um-pill um-pill--off">Inactive</span>}
          </div>
          <p className="um-banner__meta">
            {user?.email || "—"} &nbsp;·&nbsp;
            <span className="um-mono">{user?.phone || "—"}</span>
            {addedDate ? ` · Added ${addedDate}` : ""}
          </p>
        </div>
      </div>

      {/* Account details */}
      <div className="um-card">
        <div className="um-card__head">
          <span className="um-card__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </span>
          <h3 className="um-card__title">Account Details</h3>
        </div>
        <div className="um-card__body um-fields">
          <DataField label="Name" value={user?.name} />
          <DataField label="Email" value={user?.email} />
          <DataField label="Contact Number" value={user?.phone} mono />
          <DataField label="Role" value={user?.role} />
          <DataField label="Designation" value={user?.designation} />
          <DataField label="Date & Time" value={formatDate(user?.created_at)} mono />
        </div>
      </div>

      {/* Access & Permissions */}
      <div className="um-card">
        <div className="um-card__head">
          <span className="um-card__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </span>
          <h3 className="um-card__title">Access &amp; Permissions</h3>
        </div>
        <div className="um-card__body">
          {permissions.length === 0 ? (
            <div className="um-empty">
              <p className="um-empty__title">No permissions assigned</p>
              <p className="um-empty__text">This user has no module permissions yet.</p>
            </div>
          ) : (
            <>
              <div className="um-perm-block">
                <div className="um-perm-cap">
                  Modules <b>({moduleNames.length})</b>
                </div>
                <div className="um-chips">
                  {moduleNames.map((m) => (
                    <span className="um-chip" key={m}>{m}</span>
                  ))}
                </div>
              </div>
              <div className="um-perm-block">
                <div className="um-perm-cap">
                  Sub Modules <b>({subModuleNames.length})</b>
                </div>
                <div className="um-chips">
                  {subModuleNames.map((s) => (
                    <span className="um-chip" key={s}>{s}</span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagementShow;
